"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Box,
  ButtonBase,
  Portal,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material";

const VIEW_SIZE = 800;
const AVATAR_CLEAR_RADIUS = 156;
const MAX_NETWORK_PITCH = 0.38;
const SETUP_STEP_INTERVAL_MS = 7000;
const DESKTOP_SETUP_STEP_INTERVAL_MS = 12000;
const FINAL_STEP_REST_MS = 1500;

const FEMALE_FIRST_NAMES = [
  "Ava",
  "Emma",
  "Olivia",
  "Charlotte",
  "Amelia",
  "Sophia",
  "Mia",
  "Isabella",
  "Evelyn",
  "Harper",
  "Luna",
  "Emily",
  "Elizabeth",
  "Eleanor",
  "Ella",
  "Avery",
  "Scarlett",
  "Grace",
  "Chloe",
  "Victoria",
  "Madison",
  "Nora",
  "Lily",
  "Zoey",
  "Hannah",
  "Addison",
  "Stella",
  "Lucy"
];

const MALE_FIRST_NAMES = [
  "Noah",
  "Oliver",
  "James",
  "Elijah",
  "Lucas",
  "Henry",
  "Alexander",
  "Mason",
  "Michael",
  "Ethan",
  "Daniel",
  "Jacob",
  "Logan",
  "Jackson",
  "Levi",
  "Sebastian",
  "Jack",
  "Owen",
  "Theodore",
  "Aiden",
  "Samuel",
  "Joseph",
  "John",
  "David",
  "Wyatt",
  "Matthew",
  "Luke",
  "Carter"
];

const LAST_NAMES = [
  "Smith",
  "Johnson",
  "Williams",
  "Brown",
  "Jones",
  "Miller",
  "Davis",
  "Wilson",
  "Anderson",
  "Taylor",
  "Thomas",
  "Moore",
  "Martin",
  "Jackson",
  "Thompson",
  "White",
  "Harris",
  "Clark",
  "Lewis",
  "Robinson",
  "Walker",
  "Young",
  "Allen",
  "King",
  "Wright",
  "Scott",
  "Green",
  "Baker",
  "Adams",
  "Nelson",
  "Hill",
  "Campbell"
];

function createNetwork(initialSeed = 20261016) {
  const nodes = [{ x: 400, y: 400, radius: 56, meex: true }];
  const hubColors = [
    "#e94823",
    "#d79b32",
    "#5792bb",
    "#cf7385",
    "#69a69b",
    "#d28450",
    "#6f99ac",
    "#c57855"
  ];
  let seed = initialSeed;
  const random = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };

  for (let nameIndex = 0; nameIndex < 56; nameIndex += 1) {
    const angle = random() * Math.PI * 2;
    const radius = 104 + Math.sqrt(random()) * 218;
    const depth = (random() - 0.5) * 200;
    const firstNameIndex = Math.floor(nameIndex / 2);
    const firstName =
      nameIndex % 2 === 0
        ? FEMALE_FIRST_NAMES[firstNameIndex]
        : MALE_FIRST_NAMES[firstNameIndex];
    nodes.push({
      x: 400 + Math.cos(angle) * radius,
      y: 400 + Math.sin(angle) * radius,
      z: depth,
      radius: nameIndex < hubColors.length ? 16 : 4,
      kind: "profile",
      ringColor: hubColors[nameIndex] || null,
      name: `${firstName} ${LAST_NAMES[(nameIndex * 7) % LAST_NAMES.length]}`
    });
  }

  // A small deterministic relaxation gives the names a loose force layout
  // without adding a continuously running simulation to the loading screen.
  for (let iteration = 0; iteration < 90; iteration += 1) {
    for (let index = 1; index < nodes.length; index += 1) {
      const node = nodes[index];
      let pushX = 0;
      let pushY = 0;
      for (let otherIndex = 0; otherIndex < nodes.length; otherIndex += 1) {
        if (index === otherIndex) continue;
        const other = nodes[otherIndex];
        let dx = node.x - other.x;
        let dy = node.y - other.y;
        let distance = Math.max(1, Math.hypot(dx, dy));
        const isProfilePair =
          node.kind === "profile" && other.kind === "profile";
        const minimumDistance = other.meex
          ? 132
          : isProfilePair
          ? node.ringColor || other.ringColor
            ? 88
            : 78
          : 48;
        if (distance < minimumDistance) {
          const force = (minimumDistance - distance) / distance;
          pushX += dx * force * 0.15;
          pushY += dy * force * 0.15;
        }
      }
      const centerDx = node.x - VIEW_SIZE / 2;
      const centerDy = node.y - VIEW_SIZE / 2;
      const centerDistance = Math.max(1, Math.hypot(centerDx, centerDy));
      if (centerDistance < 120) {
        pushX += (centerDx / centerDistance) * (120 - centerDistance) * 0.24;
        pushY += (centerDy / centerDistance) * (120 - centerDistance) * 0.24;
      }
      node.x += pushX;
      node.y += pushY;
      const nextDx = node.x - 400;
      const nextDy = node.y - 400;
      const nextDistance = Math.hypot(nextDx, nextDy);
      if (nextDistance > 322) {
        node.x = 400 + (nextDx / nextDistance) * 322;
        node.y = 400 + (nextDy / nextDistance) * 322;
      }
    }
  }

  for (let dotIndex = 0; dotIndex < 96; dotIndex += 1) {
    const angle = random() * Math.PI * 2;
    const radius = 132 + Math.sqrt(random()) * 230;
    nodes.push({
      x: 400 + Math.cos(angle) * radius,
      y: 400 + Math.sin(angle) * radius,
      z: (random() - 0.5) * 200,
      radius: 1.8 + random() * 1.5,
      kind: "dot"
    });
  }

  const edgeKeys = new Set();
  const edges = [];
  const addEdge = (from, to) => {
    const key = `${Math.min(from, to)}:${Math.max(from, to)}`;
    if (edgeKeys.has(key)) return;
    edgeKeys.add(key);
    edges.push([from, to]);
  };

  // Build a connected backbone from the signed-in person, then add local links
  // so the graph reads as a dense community instead of a spoke diagram.
  nodes.forEach((node, index) => {
    if (node.meex) return;
    const nearestEarlier = nodes
      .slice(0, index)
      .map((candidate, candidateIndex) => ({
        index: candidateIndex,
        distance: Math.hypot(candidate.x - node.x, candidate.y - node.y)
      }))
      .sort((a, b) => a.distance - b.distance)[0];

    if (nearestEarlier) addEdge(index, nearestEarlier.index);

    const nearest = nodes
      .map((candidate, candidateIndex) => ({
        index: candidateIndex,
        distance: Math.hypot(candidate.x - node.x, candidate.y - node.y)
      }))
      .filter(candidate => candidate.index !== index)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, node.kind === "dot" ? 3 : 4);

    nearest.forEach(candidate => addEdge(index, candidate.index));
  });

  nodes.forEach((node, index) => {
    if (node.ringColor) addEdge(0, index);
  });

  const neighbors = nodes.map(() => new Set());
  edges.forEach(([from, to]) => {
    neighbors[from].add(to);
    neighbors[to].add(from);
  });

  return { nodes, edges, neighbors };
}

const INITIAL_NETWORK = createNetwork();

const NODE_POSITION_TRANSITION = {
  x: { type: "spring", stiffness: 78, damping: 23, mass: 1 },
  y: { type: "spring", stiffness: 78, damping: 23, mass: 1 },
  opacity: { duration: 0.32 },
  scale: { type: "spring", stiffness: 300, damping: 26 }
};
const EDGE_TRANSITION = {
  type: "spring",
  stiffness: 82,
  damping: 25,
  mass: 0.9
};

const MEEX_MARK_PATHS = [
  "M34.15,6.92C31.75,2.78,27.28,0,22.15,0c-2.43,0-4.7.62-6.69,1.72.81-.22,1.66-.33,2.53-.33,5.35,0,9.69,4.34,9.69,9.69v20.77c0,2.29,1.86,4.15,4.15,4.15h0c2.29,0,4.15-1.86,4.15-4.15V13.85c0-2.52-.67-4.89-1.85-6.92Z",
  "M4.19,13.85h0C1.89,13.85.03,15.71.03,18v13.85c0,2.29,1.86,4.15,4.15,4.15h0c2.29,0,4.15-1.86,4.15-4.15v-13.85c0-2.29-1.86-4.15-4.15-4.15Z",
  "M8.31,0C5.88,0,3.6.62,1.62,1.72c.81-.22,1.66-.33,2.53-.33,5.35,0,9.69,4.34,9.69,9.69v20.77c0,2.29,1.86,4.15,4.15,4.15h0c2.29,0,4.15-1.86,4.15-4.15V13.85c0-2.52-.67-4.89-1.85-6.92C17.91,2.78,13.43,0,8.31,0Z"
];

function MeexLogo({ isComplete, size = 90 }) {
  return (
    <svg
      aria-label="Meex"
      role="img"
      viewBox="0 0 36 36"
      width={size}
      height={size}
      style={{ overflow: "visible" }}
    >
      <g fill="none" stroke="#e94823" strokeWidth="2.5" opacity=".42">
        {MEEX_MARK_PATHS.map(path => (
          <path key={path} d={path} />
        ))}
        <circle cx="4.14" cy="8.31" r="4.14" />
      </g>
      {!isComplete && (
        <g
          fill="none"
          stroke="#e94823"
          strokeWidth="2.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {MEEX_MARK_PATHS.map((path, index) => (
            <path
              key={path}
              className="meex-logo-trace"
              d={path}
              pathLength="1"
              style={{ animationDelay: `${index * 180}ms` }}
            />
          ))}
          <circle
            className="meex-logo-trace"
            cx="4.14"
            cy="8.31"
            r="4.14"
            pathLength="1"
            style={{ animationDelay: "540ms" }}
          />
        </g>
      )}
      <g
        className={`meex-logo-fill${isComplete ? " meex-logo-fill-done" : ""}`}
        fill="#e94823"
      >
        {MEEX_MARK_PATHS.map(path => (
          <path key={path} d={path} />
        ))}
        <circle cx="4.14" cy="8.31" r="4.14" />
      </g>
    </svg>
  );
}

function PersonNode({
  node,
  index,
  position,
  imageSrc,
  userName,
  highlighted,
  hovered,
  isComplete,
  offset,
  fontFamily,
  onRotateStart
}) {
  const [loadedAvatarSrc, setLoadedAvatarSrc] = useState(null);
  const isAvatarImageLoaded = Boolean(
    imageSrc && loadedAvatarSrc === imageSrc
  );

  return (
    <motion.g
      initial={false}
      animate={{
        x: position.x + offset.x,
        y: position.y + offset.y,
        scale:
          (hovered ? 1.18 : 1) *
          (position.scale || 1) *
          (isComplete && !node.meex ? 0.96 : 1),
        opacity: isComplete && !node.meex ? 0 : 1
      }}
      whileHover={
        isComplete
          ? undefined
          : { scale: node.meex ? 1.05 : node.kind === "dot" ? 1.7 : 1.18 }
      }
      transition={NODE_POSITION_TRANSITION}
      onPointerDown={event => {
        event.stopPropagation();
        onRotateStart(event);
      }}
      style={{
        cursor: hovered ? "default" : "inherit",
        outline: "none",
        touchAction: "none",
        userSelect: "none"
      }}
    >
      {node.meex && (
        <>
          <circle
            r={node.radius + 13}
            fill="none"
            stroke="#d79b32"
            strokeWidth="1.5"
            opacity=".55"
          />
          <circle
            r={node.radius + (hovered ? 9 : 7)}
            fill="none"
            stroke="#e94823"
            strokeWidth="2"
            opacity=".66"
          />
          <circle
            r={node.radius}
            fill="#fff"
            stroke="#fff"
            strokeWidth="5"
            style={{
              filter: "drop-shadow(0 8px 20px rgba(17, 24, 39, .12))"
            }}
          />
          <circle
            r={node.radius}
            fill="none"
            stroke="#d8dde2"
            strokeWidth="1.5"
          />
        </>
      )}
      {!node.meex && node.ringColor && (
        <g>
          <circle
            r={node.radius + 5}
            fill={node.ringColor}
            fillOpacity=".07"
            stroke={node.ringColor}
            strokeOpacity={hovered || highlighted ? ".78" : ".52"}
            strokeWidth="1.4"
          />
          <circle r="3.5" fill={node.ringColor} fillOpacity=".9" />
        </g>
      )}
      {node.kind === "dot" ? (
        <circle
          r={node.radius}
          fill={hovered || highlighted ? "#e94823" : "#7d8994"}
          opacity={hovered || highlighted ? ".9" : ".64"}
        />
      ) : node.meex ? (
        <g>
          <circle r={node.radius - 2} fill="#f0f2f4" />
          <path
            d="M0-17a8 8 0 1 0 0 16 8 8 0 0 0 0-16Zm-15 31c1-9 7-14 15-14s14 5 15 14v3h-30Z"
            fill="#222a35"
            transform="translate(0 2) scale(.86)"
            opacity={isAvatarImageLoaded ? 0 : 1}
            style={{ transition: "opacity 650ms cubic-bezier(.2,.75,.25,1)" }}
          />
          {imageSrc && (
            <g>
              <defs>
                <clipPath id={`setup-avatar-${index}`}>
                  <circle r={node.radius - 3} />
                </clipPath>
              </defs>
              <image
                href={imageSrc}
                x={-node.radius + 3}
                y={-node.radius + 3}
                width={(node.radius - 3) * 2}
                height={(node.radius - 3) * 2}
                preserveAspectRatio="xMidYMid slice"
                clipPath={`url(#setup-avatar-${index})`}
                opacity={isAvatarImageLoaded ? 1 : 0}
                onLoad={() => setLoadedAvatarSrc(imageSrc)}
                style={{ transition: "opacity 650ms cubic-bezier(.2,.75,.25,1)" }}
              />
            </g>
          )}
        </g>
      ) : (
        <g>
          {!node.ringColor && (
            <circle
              r={node.radius}
              fill={hovered || highlighted ? "#e94823" : "#7d8994"}
              opacity={hovered || highlighted ? ".84" : ".48"}
            />
          )}
          <text
            x={
              position.x > VIEW_SIZE * 0.85
                ? -12
                : position.x < VIEW_SIZE * 0.15
                ? 12
                : 0
            }
            y={
              node.ringColor
                ? position.y > VIEW_SIZE * 0.78
                  ? -27
                  : 27
                : -8
            }
            textAnchor={
              position.x > VIEW_SIZE * 0.85
                ? "end"
                : position.x < VIEW_SIZE * 0.15
                ? "start"
                : "middle"
            }
            dominantBaseline="central"
            fill={hovered ? "#202833" : node.ringColor ? "#303a45" : "#66717b"}
            fontSize={hovered || node.ringColor ? "12" : "10.5"}
            fontWeight={hovered || node.ringColor ? "600" : "400"}
            opacity={hovered || node.ringColor ? 1 : 0.82}
            stroke="#f4f6f5"
            strokeWidth="2.5"
            paintOrder="stroke"
            letterSpacing="-0.018em"
            style={{ fontFamily }}
          >
            {node.name}
          </text>
        </g>
      )}
      <circle r={node.radius + 18} fill="transparent" />
      {node.meex && (
        <text
          y={node.radius + 24}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#303a45"
          fontSize="15"
          fontWeight="600"
        >
          {userName || "You"}
        </text>
      )}
      {node.meex && (
        <circle
          cx={node.radius * 0.72}
          cy={-node.radius * 0.72}
          r="5.5"
          fill="#e94823"
          stroke="#fff"
          strokeWidth="2.5"
        />
      )}
    </motion.g>
  );
}

export default function EnrichmentLoadingScreen({
  isComplete = false,
  userImage = null,
  userName = "",
  onBackdropClick = () => {},
  onIntroTextComplete = () => {},
  onCompletionAnimationComplete = () => {}
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [networkExpanded, setNetworkExpanded] = useState(false);
  const [network, setNetwork] = useState(INITIAL_NETWORK);
  const [activeSetupStep, setActiveSetupStep] = useState(1);
  const [finalStepDrop, setFinalStepDrop] = useState(0);
  const [finalStepDropReady, setFinalStepDropReady] = useState(false);
  const setupStepStartedAtRef = useRef(Date.now());
  const nodes = network.nodes;
  const [hoveredNode, setHoveredNode] = useState(null);
  const hoveredNeighbors = useMemo(
    () =>
      hoveredNode === null
        ? new Set()
        : network.neighbors[hoveredNode] || new Set(),
    [hoveredNode, network]
  );
  const networkRef = useRef(null);
  const surfaceRef = useRef(null);
  const rotateDragRef = useRef(null);
  const [rotation, setRotation] = useState({ yaw: 0, pitch: 0 });
  const [networkScale, setNetworkScale] = useState(1);
  const [idlePhase, setIdlePhase] = useState(0);
  const [magnetPoint, setMagnetPoint] = useState(null);
  const [pointerTrail, setPointerTrail] = useState(null);
  const pointerTrailRef = useRef([]);
  const pointerTrailTimerRef = useRef(null);
  const pointerTrailFrameRef = useRef(null);
  const latestPointerPointRef = useRef(null);
  const lastPointerTrailFrameRef = useRef(0);
  const introCallbackRef = useRef(onIntroTextComplete);
  const completionCallbackRef = useRef(onCompletionAnimationComplete);
  const hasRandomizedNetworkRef = useRef(false);

  useEffect(() => {
    if (hasRandomizedNetworkRef.current) return;
    hasRandomizedNetworkRef.current = true;
    const seed = Math.floor(Math.random() * 2147483646) + 1;
    setNetwork(createNetwork(seed));
  }, []);

  useEffect(() => {
    introCallbackRef.current = onIntroTextComplete;
  }, [onIntroTextComplete]);

  useEffect(() => {
    completionCallbackRef.current = onCompletionAnimationComplete;
  }, [onCompletionAnimationComplete]);

  useEffect(() => {
    const lastStepIndex = isDesktop ? 2 : 5;
    if (activeSetupStep >= lastStepIndex) return undefined;
    const stepInterval = isDesktop
      ? DESKTOP_SETUP_STEP_INTERVAL_MS
      : SETUP_STEP_INTERVAL_MS;
    const remaining = Math.max(
      0,
      stepInterval - (Date.now() - setupStepStartedAtRef.current)
    );
    const timer = setTimeout(() => {
      setupStepStartedAtRef.current = Date.now();
      setActiveSetupStep(activeSetupStep + 1);
    }, remaining);
    return () => clearTimeout(timer);
  }, [activeSetupStep, isDesktop]);

  useEffect(() => {
    const lastStepIndex = isDesktop ? 2 : 5;
    if (!isComplete) return undefined;

    if (activeSetupStep < lastStepIndex) {
      setupStepStartedAtRef.current = Date.now();
      setActiveSetupStep(lastStepIndex);
      return undefined;
    }

    let frame = null;
    let finishTimer = null;
    const remaining = Math.max(
      0,
      FINAL_STEP_REST_MS - (Date.now() - setupStepStartedAtRef.current)
    );
    const stageTimer = setTimeout(() => {
      if (isDesktop) {
        setFinalStepDropReady(true);
        finishTimer = setTimeout(
          () => completionCallbackRef.current?.(),
          350
        );
        return;
      }
      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      if (reducedMotion) {
        setFinalStepDropReady(true);
        finishTimer = setTimeout(
          () => completionCallbackRef.current?.(),
          80
        );
        return;
      }

      frame = window.requestAnimationFrame(() => {
        const finalStep = surfaceRef.current?.querySelector(
          "[data-final-setup-step]"
        );
        if (finalStep) {
          const bounds = finalStep.getBoundingClientRect();
          setFinalStepDrop(Math.max(0, window.innerHeight - bounds.top + 48));
        }
        setFinalStepDropReady(true);
        finishTimer = setTimeout(
          () => completionCallbackRef.current?.(),
          1630
        );
      });
    }, remaining);

    return () => {
      clearTimeout(stageTimer);
      if (frame !== null) window.cancelAnimationFrame(frame);
      if (finishTimer !== null) clearTimeout(finishTimer);
    };
  }, [isComplete, activeSetupStep, isDesktop]);

  useEffect(() => {
    if (!isComplete) {
      setFinalStepDropReady(false);
      setFinalStepDrop(0);
    }
  }, [isComplete]);

  useEffect(() => {
    const timer = setTimeout(() => introCallbackRef.current?.(), 1200);
    return () => clearTimeout(timer);
  }, []);


  useEffect(() => {
    if (
      isComplete ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }
    const timer = setInterval(() => setIdlePhase(phase => phase + 0.08), 80);
    return () => clearInterval(timer);
  }, [isComplete]);

  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return undefined;

    let frame = null;
    const target = { x: 72, y: 34 };
    const current = { x: 72, y: 34 };
    const tail = { x: 72, y: 34 };
    const animateSheen = () => {
      current.x += (target.x - current.x) * 0.18;
      current.y += (target.y - current.y) * 0.18;
      tail.x += (current.x - tail.x) * 0.065;
      tail.y += (current.y - tail.y) * 0.065;
      surface.style.setProperty("--foil-x", `${current.x}%`);
      surface.style.setProperty("--foil-y", `${current.y}%`);
      surface.style.setProperty("--foil-tail-x", `${tail.x}%`);
      surface.style.setProperty("--foil-tail-y", `${tail.y}%`);
      surface.style.setProperty(
        "--shine-angle",
        `${105 + current.x * 0.8 + (current.y - 50) * 0.18}deg`
      );
      if (
        Math.abs(target.x - current.x) + Math.abs(target.y - current.y) >
        0.08
      ) {
        frame = window.requestAnimationFrame(animateSheen);
      } else {
        frame = null;
      }
    };

    const handlePointerMove = event => {
      const bounds = surface.getBoundingClientRect();
      target.x = Math.max(
        0,
        Math.min(100, ((event.clientX - bounds.left) / bounds.width) * 100)
      );
      target.y = Math.max(
        0,
        Math.min(100, ((event.clientY - bounds.top) / bounds.height) * 100)
      );
      if (frame === null) frame = window.requestAnimationFrame(animateSheen);
    };

    surface.addEventListener("pointermove", handlePointerMove, {
      passive: true
    });
    return () => {
      surface.removeEventListener("pointermove", handlePointerMove);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    const handlePointerMove = event => {
      if (event.pointerType === "touch") return;
      latestPointerPointRef.current = [event.clientX, event.clientY];
      if (pointerTrailFrameRef.current !== null) return;
      pointerTrailFrameRef.current = window.requestAnimationFrame(timestamp => {
        pointerTrailFrameRef.current = null;
        if (timestamp - lastPointerTrailFrameRef.current < 32) return;
        lastPointerTrailFrameRef.current = timestamp;
        const point = latestPointerPointRef.current;
        if (!point) return;
        const points = pointerTrailRef.current;
        points.push(point);
        if (points.length > 24) points.shift();
        setPointerTrail({
          width: window.innerWidth,
          height: window.innerHeight,
          points: [...points]
        });
        clearTimeout(pointerTrailTimerRef.current);
        pointerTrailTimerRef.current = setTimeout(() => {
          pointerTrailRef.current = [];
          setPointerTrail(null);
        }, 900);
      });
    };

    window.addEventListener("pointermove", handlePointerMove, {
      passive: true
    });
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      clearTimeout(pointerTrailTimerRef.current);
      if (pointerTrailFrameRef.current !== null) {
        window.cancelAnimationFrame(pointerTrailFrameRef.current);
      }
    };
  }, []);

  const handleRotateStart = event => {
    if (event.button !== undefined && event.button !== 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    rotateDragRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY
    };
  };

  const handleNetworkPointerMove = event => {
    if (!networkRef.current) return;
    const bounds = networkRef.current.getBoundingClientRect();
    if (!rotateDragRef.current) {
      if (event.pointerType !== "touch") {
        const svg = networkRef.current;
        const matrix = svg.getScreenCTM();
        if (matrix) {
          const screenPoint = svg.createSVGPoint();
          screenPoint.x = event.clientX;
          screenPoint.y = event.clientY;
          const point = screenPoint.matrixTransform(matrix.inverse());
          let nearestIndex = null;
          let nearestDistance = 56;
          nodes.forEach((node, index) => {
            if (index === 0 || node.kind !== "profile") return;
            const position = getCameraPosition(index);
            const distance = Math.hypot(
              position.x - point.x,
              position.y - point.y
            );
            if (distance < nearestDistance) {
              nearestIndex = index;
              nearestDistance = distance;
            }
          });
          setHoveredNode(nearestIndex);
          setMagnetPoint(current => {
            if (nearestIndex === null) return current === null ? current : null;
            if (
              current &&
              Math.hypot(current.x - point.x, current.y - point.y) < 2
            ) {
              return current;
            }
            return { x: point.x, y: point.y };
          });
        }
      }
      return;
    }
    const previous = rotateDragRef.current;
    const deltaX = event.clientX - previous.x;
    const deltaY = event.clientY - previous.y;
    rotateDragRef.current = {
      ...previous,
      x: event.clientX,
      y: event.clientY
    };
    setRotation(current => ({
      yaw: current.yaw + (deltaX / Math.max(bounds.width, 1)) * Math.PI * 2,
      pitch: Math.max(
        -MAX_NETWORK_PITCH,
        Math.min(
          MAX_NETWORK_PITCH,
          current.pitch + (deltaY / Math.max(bounds.height, 1)) * 0.65
        )
      )
    }));
  };

  const handleNetworkPointerLeave = () => {
    if (rotateDragRef.current) return;
    setHoveredNode(null);
    setMagnetPoint(null);
  };

  const stopRotating = () => {
    rotateDragRef.current = null;
  };

  const handleNetworkWheel = event => {
    event.preventDefault();
    setNetworkScale(current =>
      Math.max(0.72, Math.min(1.8, current * Math.exp(-event.deltaY * 0.001)))
    );
  };

  const activeCopy = isComplete && finalStepDropReady
    ? {
        title: "You’re all set.",
        description: "Your account is ready. Explore Meex now."
      }
    : {
        title: "Almost there.",
        description:
          "We’re preparing your account. Explore the network while we finish."
      };
  const mobileSetupStepLabels = [
    "Sign-in complete",
    "Creating your account",
    "Connecting your network",
    "Finishing setup",
    "Ready to explore"
  ];
  const desktopSetupStepLabels = [
    "Sign-in complete",
    "Setting things up",
    "Ready to explore"
  ];
  const setupStepLabels = isDesktop
    ? desktopSetupStepLabels
    : mobileSetupStepLabels;
  const displayedActiveSetupStep = isDesktop
    ? Math.min(activeSetupStep, 2)
    : activeSetupStep;
  const setupSteps = setupStepLabels.map((label, index) => ({
    label,
    status:
      index === 0 ||
      index < displayedActiveSetupStep ||
      (index === setupStepLabels.length - 1 && finalStepDropReady)
        ? "complete"
        : index === displayedActiveSetupStep
          ? "active"
          : "upcoming"
  }));
  const getStepTextOpacity = index => {
    if (index === displayedActiveSetupStep) return 1;
    if (isDesktop) return index < displayedActiveSetupStep ? 0.7 : 0.58;
    if (index < displayedActiveSetupStep) {
      return Math.max(0.66, 0.9 - (displayedActiveSetupStep - index) * 0.06);
    }
    return Math.max(
      0.32,
      0.76 - (index - displayedActiveSetupStep) * 0.1
    );
  };
  const isConnectedToHovered = index => hoveredNeighbors.has(index);
  const getBaseCameraPosition = index => {
    const node = nodes[index];
    if (index === 0) return { ...node, scale: 1 };
    const cosYaw = Math.cos(rotation.yaw);
    const sinYaw = Math.sin(rotation.yaw);
    const cosPitch = Math.cos(rotation.pitch);
    const sinPitch = Math.sin(rotation.pitch);
    const dx = node.x - 400;
    const dy = node.y - 400;
    const dz = node.z || 0;
    const rotatedX = dx * cosYaw + dz * sinYaw;
    const rotatedZ = -dx * sinYaw + dz * cosYaw;
    const rotatedY = dy * cosPitch - rotatedZ * sinPitch;
    const depth = dy * sinPitch + rotatedZ * cosPitch;
    const perspective = Math.max(0.78, Math.min(1.24, 1900 / (1900 - depth)));
    const idleIndex = 1 + (Math.floor(idlePhase / 2.2) % 56);
    const idleProgress = (idlePhase % 2.2) / 1.1;
    const idleAmount =
      idleProgress <= 1 ? Math.sin(idleProgress * Math.PI) * 4.5 : 0;
    const idleX = index === idleIndex ? Math.cos(index * 1.7) * idleAmount : 0;
    const idleY = index === idleIndex ? Math.sin(index * 2.1) * idleAmount : 0;
    let x = Math.max(
      80,
      Math.min(
        VIEW_SIZE - 80,
        400 + rotatedX * perspective * networkScale + idleX
      )
    );
    let y = Math.max(
      48,
      Math.min(
        VIEW_SIZE - 72,
        400 + rotatedY * perspective * networkScale + idleY
      )
    );
    if (node.kind === "profile") {
      const centerX = x - 400;
      const centerY = y - 400;
      const centerDistance = Math.hypot(centerX, centerY);
      if (centerDistance < AVATAR_CLEAR_RADIUS) {
        const distance = Math.max(1, centerDistance);
        x = 400 + (centerX / distance) * AVATAR_CLEAR_RADIUS;
        y = 400 + (centerY / distance) * AVATAR_CLEAR_RADIUS;
      }
    }
    return {
      x,
      y,
      scale: perspective
    };
  };
  const getCameraPosition = getBaseCameraPosition;
  const getNodeOffset = index => {
    if (index === 0) {
      return { x: 0, y: 0 };
    }
    const node = getCameraPosition(index);
    let offset = { x: 0, y: 0 };
    if (hoveredNode === index && magnetPoint) {
      const dx = magnetPoint.x - node.x;
      const dy = magnetPoint.y - node.y;
      const distance = Math.hypot(dx, dy);
      const attraction = Math.min(18, distance * 0.32);
      if (distance) {
        offset = {
          x: (dx / distance) * attraction,
          y: (dy / distance) * attraction
        };
      }
    } else if (isConnectedToHovered(index)) {
      const focus = getCameraPosition(hoveredNode);
      const dx = node.x - focus.x;
      const dy = node.y - focus.y;
      const distance = Math.max(1, Math.hypot(dx, dy));
      offset = { x: (dx / distance) * -8, y: (dy / distance) * -8 };
    }

    if (node.kind !== "profile") return offset;
    const targetX = node.x + offset.x - 400;
    const targetY = node.y + offset.y - 400;
    const targetDistance = Math.hypot(targetX, targetY);
    if (targetDistance >= AVATAR_CLEAR_RADIUS) return offset;
    const distance = Math.max(1, targetDistance);
    return {
      x: (targetX / distance) * AVATAR_CLEAR_RADIUS - (node.x - 400),
      y: (targetY / distance) * AVATAR_CLEAR_RADIUS - (node.y - 400)
    };
  };
  return (
    <Portal>
      <Box
        ref={surfaceRef}
        component="main"
        sx={{
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100dvh",
          maxWidth: "100vw",
          boxSizing: "border-box",
          overflow: "hidden",
          zIndex: 2147483647,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(390px, 0.78fr) minmax(0, 1.22fr)"
          },
          gridTemplateRows: {
            xs: "minmax(0, 1fr)",
            md: "1fr"
          },
          background: "linear-gradient(135deg, #f9faf9 0%, #f0f2f1 100%)",
          opacity: isComplete && finalStepDropReady ? 0 : 1,
          transition: isComplete && finalStepDropReady
            ? isDesktop
              ? "opacity 260ms ease"
              : "opacity 260ms ease 1300ms"
            : "opacity 360ms ease",
          "& > section, & > [aria-label='Interactive map of connections']": {
            position: "relative",
            zIndex: 1
          },
          "& .meex-logo-trace": {
            strokeDasharray: "0.2 0.8",
            animation: "meex-logo-trace 4.5s linear infinite"
          },
          "& .meex-logo-fill": {
            opacity: 0,
            transition: "opacity 280ms ease"
          },
          "& .meex-logo-fill-done": { opacity: 1 },
          "@keyframes meex-logo-trace": {
            to: { strokeDashoffset: -1 }
          },
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
            transition: "none",
            "& .meex-logo-trace": { animation: "none !important" },
            "& .meex-logo-fill": { opacity: "1 !important" }
          }
        }}
        onClick={onBackdropClick}
      >
        <Box
          aria-hidden="true"
          sx={{
            display: { xs: "none", md: "block" },
            position: "absolute",
            inset: 0,
            zIndex: 2,
            pointerEvents: "none",
            clipPath: { xs: "none", md: "inset(0 0 0 38%)" },
            opacity: 0.56,
            background:
              "radial-gradient(ellipse 27% 33% at var(--foil-tail-x, 72%) var(--foil-tail-y, 50%), rgba(233,72,35,.12) 0%, rgba(215,155,50,.09) 34%, transparent 76%), radial-gradient(ellipse 23% 25% at var(--foil-x, 72%) var(--foil-y, 50%), rgba(215,155,50,.12) 0%, rgba(233,72,35,.06) 38%, transparent 78%), radial-gradient(ellipse 14% 16% at var(--foil-x, 72%) var(--foil-y, 50%), rgba(255,255,255,.58) 0%, rgba(233,72,35,.05) 42%, transparent 78%)",
            filter: "saturate(82%)",
            mixBlendMode: "multiply"
          }}
        />
        {pointerTrail && pointerTrail.points.length > 1 && (
          <svg
            aria-hidden="true"
            viewBox={`0 0 ${pointerTrail.width} ${pointerTrail.height}`}
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 4,
              width: "100%",
              height: "100%",
              overflow: "visible",
              pointerEvents: "none"
            }}
          >
            <polyline
              points={pointerTrail.points
                .map(point => point.join(","))
                .join(" ")}
              fill="none"
              stroke="rgba(233, 72, 35, .055)"
              strokeWidth="38"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ filter: "blur(20px)" }}
            />
          </svg>
        )}
        <Box
          aria-label="Interactive map of connections"
          sx={{
            display: { xs: "none", md: "block" },
            position: {
              xs: networkExpanded ? "fixed" : "relative",
              md: "relative"
            },
            ...(networkExpanded && {
              inset: { xs: 0, md: "auto" },
              zIndex: { xs: 3, md: "auto" }
            }),
            gridColumn: { xs: 1, md: 2 },
            gridRow: { xs: networkExpanded ? "1 / span 2" : 2, md: 1 },
            m: 0,
            minWidth: 0,
            minHeight: 0,
            overflow: "hidden",
            borderRadius: {
              xs: 0,
              md: 0
            },
            touchAction: "none",
            background: "transparent",
            boxShadow: "none"
          }}
        >
          <Box
            ref={networkRef}
            component="svg"
            role="group"
            aria-label="Drag the background to rotate the connected network."
            viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`}
            preserveAspectRatio="xMidYMid meet"
            onPointerDown={handleRotateStart}
            onPointerMove={handleNetworkPointerMove}
            onPointerUp={stopRotating}
            onPointerCancel={stopRotating}
            onPointerLeave={handleNetworkPointerLeave}
            onWheel={handleNetworkWheel}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              cursor: hoveredNode === null ? "grab" : "default"
            }}
          >
            {network.edges.map(([from, to], index) => {
              const cameraA = getCameraPosition(from);
              const cameraB = getCameraPosition(to);
              const offsetA = getNodeOffset(from);
              const offsetB = getNodeOffset(to);
              const isAvatarEdge = from === 0 || to === 0;
              const hoveredConnection =
                hoveredNode !== null &&
                (from === hoveredNode || to === hoveredNode);
              return (
                <motion.line
                  key={index}
                  initial={false}
                  animate={{
                    x1: cameraA.x + offsetA.x,
                    y1: cameraA.y + offsetA.y,
                    x2: cameraB.x + offsetB.x,
                    y2: cameraB.y + offsetB.y,
                    stroke:
                      hoveredConnection
                        ? nodes[from].ringColor ||
                          nodes[to].ringColor ||
                          "#697887"
                        : isAvatarEdge
                        ? "#d79b32"
                        : "#9ca7b1",
                    strokeWidth: hoveredConnection
                      ? 0.9
                      : isAvatarEdge
                      ? 0.72
                      : 0.55,
                    opacity: isComplete
                      ? 0
                      : hoveredConnection
                      ? 0.84
                      : isAvatarEdge
                      ? 0.24
                      : 0.15
                  }}
                  transition={EDGE_TRANSITION}
                />
              );
            })}
            {nodes.map((node, index) => (
              <PersonNode
                key={index}
                node={node}
                index={index}
                position={getCameraPosition(index)}
                imageSrc={node.meex ? userImage : null}
                userName={userName}
                highlighted={
                  node.meex ||
                  hoveredNode === index ||
                  isConnectedToHovered(index)
                }
                hovered={hoveredNode === index}
                isComplete={isComplete}
                offset={getNodeOffset(index)}
                fontFamily={theme.typography.fontFamily}
                onRotateStart={handleRotateStart}
              />
            ))}
          </Box>
          <ButtonBase
            aria-label={
              networkExpanded
                ? "Close full-screen connection map"
                : "Expand connection map"
            }
            onClick={event => {
              event.stopPropagation();
              setNetworkExpanded(value => !value);
            }}
            sx={{
              position: "absolute",
              top: { xs: 12, sm: 20 },
              right: { xs: 12, sm: 20 },
              zIndex: 2,
              display: { xs: "grid", md: "none" },
              placeItems: "center",
              width: 42,
              height: 42,
              borderRadius: "50%",
              color: "#303a45",
              backgroundColor: alpha("#fff", 0.84),
              boxShadow: "0 2px 12px rgba(17,24,32,.08)",
              backdropFilter: "blur(8px)",
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "#303a45",
                outlineOffset: 2
              }
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              {networkExpanded ? (
                <path
                  d="M8 3v5H3M16 3v5h5M3 16h5v5m13-5h-5v5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : (
                <path
                  d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
            </svg>
          </ButtonBase>
        </Box>

        <Box
          component="section"
          aria-labelledby="meex-setup-title"
          sx={{
            gridColumn: { xs: 1, md: 1 },
            gridRow: { xs: 1, md: 1 },
            position: "relative",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minWidth: 0,
            minHeight: 0,
            overflowY: "auto",
            background: {
              xs: "transparent",
              md: "linear-gradient(155deg, #1d242c 0%, #252e37 100%)"
            },
            borderRight: { xs: 0, md: "1px solid rgba(255,255,255,.08)" },
            pl: { xs: 3, sm: 6, md: 8, lg: 12 },
            pr: { xs: 3, sm: 5, lg: 8 },
            pt: { xs: 2, md: 5 },
            pb: "max(24px, env(safe-area-inset-bottom))"
          }}
        >
          <Stack
            direction="row"
            sx={{ alignItems: "center", mb: { xs: 1.5, md: 3.25 } }}
          >
            <MeexLogo isComplete={isComplete} size={isDesktop ? 64 : 90} />
          </Stack>
          <Typography
            sx={{
              display: { xs: "none", md: "block" },
              mb: 1.25,
              color: { xs: "#77818a", md: "#f17a5c" },
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: ".14em",
              textTransform: "uppercase"
            }}
          >
            Welcome to Meex
          </Typography>
          <Typography
            id="meex-setup-title"
            component="h1"
            aria-live="polite"
            sx={{
              maxWidth: 520,
              fontFamily: theme.typography.fontFamily,
              fontSize: {
                xs: "clamp(30px, 8vw, 40px)",
                md: "clamp(36px, 3.7vw, 50px)"
              },
              fontWeight: 600,
              lineHeight: 1.08,
              letterSpacing: "-0.045em",
              color: { xs: "#202833", md: "#f7f8f9" }
            }}
          >
            {activeCopy.title}
          </Typography>
          <Typography
            sx={{
              mt: 1.75,
              maxWidth: 430,
              color: { xs: "#65717f", md: "#bdc5cc" },
              fontSize: { xs: 14, md: 16 },
              lineHeight: 1.65
            }}
          >
            {activeCopy.description}
          </Typography>

          <Box
            sx={{
              mt: { xs: 3, md: 4 },
              maxWidth: 430,
              position: "relative",
              "@keyframes meex-step-progress": {
                to: { strokeDashoffset: 0 }
              },
              "@keyframes meex-step-track-pulse": {
                "0%, 100%": { strokeOpacity: 0.18 },
                "50%": { strokeOpacity: 0.42 }
              },
              "@keyframes meex-final-step-drop": {
                "0%": {
                  transform: "translateY(0px)",
                  animationTimingFunction: "cubic-bezier(.12,.72,.2,1)"
                },
                "22%": {
                  transform: "translateY(var(--final-step-early, 0px))",
                  animationTimingFunction: "linear"
                },
                "78%": {
                  transform: "translateY(var(--final-step-late, 0px))",
                  animationTimingFunction: "cubic-bezier(.65,0,.95,.3)"
                },
                "100%": {
                  transform: "translateY(var(--final-step-drop, 0px))"
                }
              }
            }}
          >
            <Stack
              component="ol"
              spacing={{ xs: 1.25, md: 1.75 }}
              sx={{
                p: 0,
                listStyle: "none",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 11,
                  top: 13,
                  bottom: { xs: "-100vh", md: 12 },
                  height: "auto",
                  width: "1px",
                  pointerEvents: "none",
                  background: {
                    xs: "linear-gradient(to bottom, #9aa3ad 0%, rgba(154,163,173,.18) 75%, transparent 100%)",
                    md: "linear-gradient(to bottom, rgba(255,255,255,.32) 0%, rgba(255,255,255,.12) 75%, transparent 100%)"
                  },
                  zIndex: 0
                }
              }}
            >
              {setupSteps.map((step, index) => {
                const complete = step.status === "complete";
                const active = step.status === "active";
                const textOpacity = getStepTextOpacity(index);
                const activeRingDuration =
                  isComplete && index === setupSteps.length - 1
                    ? FINAL_STEP_REST_MS
                    : isDesktop
                      ? DESKTOP_SETUP_STEP_INTERVAL_MS
                      : SETUP_STEP_INTERVAL_MS;
                return (
                  <Stack
                    component="li"
                    key={step.label}
                    direction="row"
                    spacing={1.25}
                    aria-current={active ? "step" : undefined}
                    sx={{
                      alignItems: "center",
                      position: "relative",
                      zIndex: 1,
                      color: { xs: "#202833", md: "#f4f6f7" },
                      transition: "transform 1300ms cubic-bezier(.4,0,.2,1)",
                      ...(!isDesktop && finalStepDropReady && index === setupSteps.length - 1 && {
                        "--final-step-drop": `${finalStepDrop}px`,
                        "--final-step-early": `${finalStepDrop * 0.38}px`,
                        "--final-step-late": `${finalStepDrop * 0.62}px`,
                        ...(finalStepDropReady && {
                          animation: "meex-final-step-drop 1300ms cubic-bezier(.4,0,.2,1) forwards"
                        })
                      }),
                      "@media (prefers-reduced-motion: reduce)": {
                        transition: "none",
                        animation: "none"
                      }
                    }}
                  >
                    <Box
                      data-final-setup-step={index === setupSteps.length - 1 || undefined}
                      aria-hidden="true"
                      sx={{
                        width: 24,
                        height: 24,
                        flex: "0 0 24px",
                        display: "grid",
                        placeItems: "center",
                        borderRadius: "50%",
                        border: "1px solid",
                        borderColor: complete
                          ? { xs: "#202833", md: "rgba(255,255,255,.18)" }
                          : active
                          ? "rgba(233,72,35,.18)"
                          : { xs: "divider", md: "rgba(255,255,255,.18)" },
                        color: complete
                          ? "#fff"
                          : active
                          ? "#e94823"
                          : {
                              xs: `rgba(32,40,51,${textOpacity})`,
                              md: `rgba(255,255,255,${textOpacity})`
                            },
                        backgroundColor: complete
                          ? { xs: "#202833", md: "#e94823" }
                          : active
                          ? { xs: "#f4f5f4", md: "rgba(233,72,35,.1)" }
                          : { xs: "#f4f5f4", md: "rgba(255,255,255,.04)" },
                        fontSize: 12,
                        fontWeight: 600,
                        position: "relative",
                        zIndex: 1
                      }}
                    >
                      {active && (
                        <Box
                          component="svg"
                          viewBox="0 0 34 34"
                          aria-hidden="true"
                          sx={{
                            position: "absolute",
                            width: 34,
                            height: 34,
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            overflow: "visible",
                            pointerEvents: "none",
                            zIndex: 2,
                            "& .meex-step-progress-indicator": {
                              animation: `meex-step-progress ${activeRingDuration}ms linear forwards`
                            },
                            "& .meex-step-track-pulse": {
                              animation:
                                "meex-step-track-pulse 1.8s ease-in-out infinite"
                            },
                            "@media (prefers-reduced-motion: reduce)": {
                              "& .meex-step-progress-indicator": {
                                animation: "none",
                                strokeDashoffset: 43.98
                              },
                              "& .meex-step-track-pulse": { animation: "none" }
                            }
                          }}
                        >
                          <circle
                            className="meex-step-track-pulse"
                            cx="17"
                            cy="17"
                            r="14"
                            fill="none"
                            stroke="rgba(233,72,35,.2)"
                            strokeWidth="2.2"
                          />
                          <circle
                            className="meex-step-progress-indicator"
                            cx="17"
                            cy="17"
                            r="14"
                            fill="none"
                            stroke="#e94823"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeDasharray="87.96"
                            strokeDashoffset="87.96"
                            transform="rotate(-90 17 17)"
                          />
                        </Box>
                      )}
                      {complete ? (
                        <Box
                          component="span"
                          sx={{ opacity: index < activeSetupStep ? 0.84 : 1 }}
                        >
                          ✓
                        </Box>
                      ) : active ? (
                        <Box
                          component="span"
                          sx={{
                            position: "relative",
                            zIndex: 3,
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            backgroundColor: "#e94823"
                          }}
                        />
                      ) : (
                        index + 1
                      )}
                    </Box>
                    <Typography
                      sx={{
                        opacity: textOpacity,
                        transition: "opacity 650ms cubic-bezier(.2,.75,.25,1)",
                        fontSize: 15,
                        fontWeight: active ? 600 : 500
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        </Box>
      </Box>
    </Portal>
  );
}
